// src/components/auth/MagicLinkHandler.jsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';


const MagicLinkHandler = ({ token, onComplete, onBack }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasPassword, setHasPassword] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const passwordRef = useRef(null);
  const { setUserData } = useAuth();

  // Enhanced password validation
  const validatePassword = (password, confirmPassword = '') => {
    const errors = {};

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])/.test(password)) {
      errors.password = 'Password must contain at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(password)) {
      errors.password = 'Password must contain at least one uppercase letter';
    } else if (!/(?=.*\d)/.test(password)) {
      errors.password = 'Password must contain at least one number';
    } else if (!/(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(password)) {
      errors.password = 'Password must contain at least one special character';
    }

    if (confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  const clearErrors = () => {
    setErrors({});
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    clearErrors();
  };

  // Validate magic link on component mount
  useEffect(() => {
    const validateMagicLink = async () => {
      if (!token) {
        showToast('Invalid magic link', 'error');
        if (onBack) onBack();
        return;
      }

      try {
        console.log(' Validating magic link...');
        setIsLoading(true);

        const response = await api.get(`${API_CONFIG.AUTH.MAGIC_VALIDATE}/${token}`);
        

        if (response.data.success && response.data.validLink) {
          if (response.data.hasPassword) {
            // User has password - automatically logged in
            setHasPassword(true);
            setUserId(response.data.userId);
            
            // Set user data in auth context
            if (response.data.user && response.data.token) {
              setUserData(response.data.user, response.data.account, response.data.token);
              showToast('Login successful!', 'success');
              
              if (onComplete) {
                onComplete();
              }
            }
          } else {
            // User needs to set password
            setHasPassword(false);
            setUserId(response.data.userId);
            setUserEmail(response.data.userEmail);
            setIsLoading(false);
          }
        } else {
          throw new Error(response.data.error || 'Invalid magic link');
        }

      } catch (error) {
        console.error('Magic link validation failed:', error);
        
        let errorMessage = 'Invalid or expired magic link';
        
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }

        showToast(errorMessage, 'error');
        
        if (onBack) {
          onBack();
        }
      } finally {
        setIsLoading(false);
      }
    };

    validateMagicLink();
  }, [token, onComplete, onBack, setUserData]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    clearErrors();

    // Validate password
    const passwordErrors = validatePassword(
      passwordData.password, 
      passwordData.confirmPassword
    );

    if (Object.keys(passwordErrors).length > 0) {
      setErrors(passwordErrors);
      showToast('Please fix the password errors', 'error');
      passwordRef.current?.focus();
      return;
    }

    try {
      console.log('Setting up password...');
      setIsSettingPassword(true);

      const response = await api.post(API_CONFIG.AUTH.MAGIC_SET_PASSWORD, {
        userId,
        password: passwordData.password,
        confirmPassword: passwordData.confirmPassword,
        magicLinkToken: token
      });

      if (response.data.success) {
        console.log('Password set successfully');
        showToast('Password set successfully! Logging you in...', 'success');

        // Set user data and log them in
        if (response.data.user && response.data.token) {
          setUserData(response.data.user, response.data.account, response.data.token);
          
          if (onComplete) {
            onComplete();
          }
        }
      } else {
        throw new Error(response.data.error || 'Failed to set password');
      }

    } catch (error) {
      console.error('Password setup failed:', error);
      
      let errorMessage = 'Failed to set password';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
      setErrors({ submit: errorMessage });
    } finally {
      setIsSettingPassword(false);
    }
  };

  const handleRetry = () => {
    clearErrors();
    if (passwordData.password) {
      handleSetPassword(new Event('submit'));
    } else {
      passwordRef.current?.focus();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600 text-center">Validating your magic link...</p>
        <p className="text-sm text-gray-500 mt-2 text-center">
          Please wait while we verify your secure login link
        </p>
      </div>
    );
  }

  // Password setup form
  if (hasPassword === false) {
    return (
      <div className="magic-link-password-setup">
        {onBack && (
          <button
            type="button"
            className="flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors disabled:opacity-50"
            onClick={onBack}
            disabled={isSettingPassword}
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Back to Login
          </button>
        )}

        <div className="text-center mb-6">
          <svg className="w-16 h-16 mx-auto text-blue-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-2xl font-bold  mb-2">Set Up Your Password</h2>
          <p className="text-gray-600 mb-2">
            Welcome! To secure your account, please create a password.
          </p>
          {userEmail && (
            <p className="text-sm text-gray-500 mb-4">
              Account: <span className="font-medium">{userEmail}</span>
            </p>
          )}
        </div>

        <form onSubmit={handleSetPassword}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                ref={passwordRef}
                id="password"
                type="password"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Enter your new password"
                value={passwordData.password}
                onChange={(e) => handlePasswordChange('password', e.target.value)}
                required
                autoComplete="new-password"
                disabled={isSettingPassword}
                aria-describedby={errors.password ? "password-error" : undefined}
              />
              {errors.password && (
                <p id="password-error" className="mt-2 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {errors.password}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Confirm your new password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                required
                autoComplete="new-password"
                disabled={isSettingPassword}
                aria-describedby={errors.confirmPassword ? "confirm-password-error" : undefined}
              />
              {errors.confirmPassword && (
                <p id="confirm-password-error" className="mt-2 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </div>

          {errors.submit && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 flex items-center">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {errors.submit}
              </p>
              <button
                type="button"
                onClick={handleRetry}
                className="mt-2 text-sm text-red-700 hover:text-red-800 underline focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
              >
                Try again
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={isSettingPassword}
            className="w-full mt-6 py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {isSettingPassword ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Setting Up Password...
              </>
            ) : (
              'Set Password & Continue'
            )}
          </button>
        </form>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            Password Requirements
          </h3>
          <ul className="text-xs text-blue-600 space-y-1">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              At least 8 characters long
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              One uppercase and one lowercase letter
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              One number and one special character
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              Make it unique and hard to guess
            </li>
          </ul>
        </div>
      </div>
    );
  }

  // Success state (auto-logged in)
  if (hasPassword === true) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 p-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold  mb-2">Login Successful!</h3>
        <p className="text-gray-600 text-center mb-6">
          You have been automatically logged in via magic link.
        </p>
        <button
          onClick={onComplete}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          Continue to Dashboard
        </button>
      </div>
    );
  }

  // Error state
  return (
    <div className="flex flex-col items-center justify-center min-h-64 p-8">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      </div>
      <h3 className="text-xl font-semibold  mb-2">Invalid Magic Link</h3>
      <p className="text-gray-600 text-center mb-6">
        This magic link is invalid or has expired.
      </p>
      {onBack && (
        <button
          onClick={onBack}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          Back to Login
        </button>
      )}
    </div>
  );
};

export default MagicLinkHandler;